import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, X, Search, Check } from "lucide-react";

const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select an option...",
  icon: Icon,
  callFun,
  disabled = false,
  onDoubleClick,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionsRef = useRef([]);

  useEffect(() => {
    const selected = options?.find((opt) => opt.value === value);
    setSelectedLabel(selected ? selected.label : "");
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options?.filter((option) =>
    option.label?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  useEffect(() => {
    const searchFunc = async () => {
      if (callFun) await callFun(searchTerm);
    };
    if (searchTerm?.length >= 2) searchFunc();
    if(searchTerm?.length === 0) {
      if(callFun) callFun("");
    };
  }, [searchTerm, callFun]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSelectedLabel("");
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleSelect(filteredOptions[focusedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSearchTerm("");
        setFocusedIndex(-1);
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && optionsRef.current[focusedIndex]) {
      optionsRef.current[focusedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [focusedIndex]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Select Button */}
      <div
        className={`
          group relative flex items-center gap-3 
          backdrop-blur-sm border ${value ? 'border-purple-500/50' : "border-slate-700/50"} 
          rounded-xl px-3 py-1.5 text-white text-sm 
          cursor-pointer min-w-[160px] transition-all duration-200
          hover:border-violet-500/50 hover:shadow-lg hover:shadow-violet-500/10
          focus-within:border-violet-500 focus-within:shadow-lg focus-within:shadow-violet-500/20
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${
            isOpen
              ? "border-violet-500 shadow-lg shadow-violet-500/20 ring-1 ring-violet-500/20"
              : ""
          }
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onDoubleClick={onDoubleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {Icon && (
          <Icon
            size={16}
            className="text-violet-400 flex-shrink-0 transition-colors duration-200 group-hover:text-violet-300"
          />
        )}

        <span className="flex-1 truncate font-medium">
          {selectedLabel || (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </span>

        <div className="flex items-center gap-2">
          {selectedLabel && !disabled && (
            <button
              onClick={handleClear}
              className="text-slate-400 hover:text-white transition-colors duration-200 p-0.5 rounded-md hover:bg-slate-700/50"
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={`
              text-slate-400 transition-all duration-200 
              group-hover:text-slate-300
              ${isOpen ? "rotate-180 text-violet-400" : ""}
            `}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute top-full w-full min-w-[260px] left-0 right-0 mt-2 bg-slate-900/95 backdrop-blur-md border border-slate-700/50 rounded-xl shadow-2xl shadow-black/50 z-50 max-h-80 overflow-hidden animate-in slide-in-from-top-2 duration-200">
          {/* Search Input */}
          <div className="p-3 border-b border-slate-700/50">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
              />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFocusedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Search options..."
                className="
                  w-full pl-10 pr-4 py-2.5 
                  bg-slate-800/50 border border-slate-600/50 rounded-lg 
                  text-white text-sm placeholder-slate-400
                  focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20
                  transition-all duration-200
                "
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-track-slate-800 scrollbar-thumb-slate-600">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  ref={(el) => (optionsRef.current[index] = el)}
                  onClick={() => handleSelect(option)}
                  className={`
                    flex items-center justify-between px-4 py-3 text-sm cursor-pointer 
                    transition-all duration-150
                    ${
                      index === focusedIndex
                        ? "bg-violet-600/20 text-violet-300"
                        : "hover:bg-slate-700/50"
                    }
                    ${
                      option.value === value
                        ? "bg-violet-600/30 text-violet-300"
                        : "text-slate-200"
                    }
                  `}
                  role="option"
                  aria-selected={option.value === value}
                >
                  <span className="font-medium">{option.label}</span>
                  {option.value === value && (
                    <Check
                      size={16}
                      className="text-violet-400 flex-shrink-0"
                    />
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center">
                <div className="text-slate-400 text-sm">
                  {searchTerm ? "No results found" : "No options available"}
                </div>
                {searchTerm && (
                  <div className="text-slate-500 text-xs mt-1">
                    Try adjusting your search term
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
